import React, {FC, PropsWithChildren, useState, useEffect, useRef, KeyboardEvent, ChangeEvent} from 'react';

import { ChevronDownIcon } from 'views/components/icons';
import { Root } from './index.styled';

interface Props extends PropsWithChildren {
  list: string[];
  index: number;
  onSelect: (index: number) => void;
  readOnly?: boolean;
  inFocus?: boolean;
  value?: string;
  onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
  placeholder?: string;
}

const SelectInput: FC<Props> = ({ children, list, index, onSelect, readOnly = true, inFocus = false, value = '', onChange = () => {}, placeholder = '' }) => {

  const [isOpen, setIsOpen] = useState(false);
  const [selected, setSelected] = useState(index);
  const input = useRef<HTMLInputElement>(null);
  const ol = useRef<HTMLOListElement>(null);

  function toggle() {
    if (!list.length) close();
    else setIsOpen(!isOpen);
  }

  function open() {
    if (!list.length) close();
    else setIsOpen(true);
  }

  function close() {
    setIsOpen(false);
  }

  function closeWithClick(e: MouseEvent) {
    close();
  }

  function back() {
    if (!isOpen) open();
    const first = 0;
    const last = list.length - 1;
    let next = selected !== -1 ? selected - 1 : last;
    if (next < first) next = last;
    select(next);
  }

  function next() {
    if (!isOpen) open();
    const first = 0;
    const last = list.length - 1;
    let next = selected !== -1 ? selected + 1 : first;
    if (next > last) next = first;
    select(next);
  }

  function watchKeyboard(e: KeyboardEvent<HTMLInputElement>) {
    switch (e.key) {
      case 'Tab': return close();
      case 'ArrowUp': return back();
      case 'ArrowDown': return next();
      case 'Enter': return toggle();
      case ' ': return toggle();
      case 'Escape': return close();
    }
  }

  function select(index: number) {
    setSelected(index);
    onSelect(index);
    input.current?.focus();
  }

  function focus() {
    if (!input.current) return;
    if (!inFocus) return;
    input.current.focus();
    close();
  }

  function setupWindowClick() {
    if (!isOpen) return;
    window.addEventListener('click', closeWithClick);
    return () => window.removeEventListener('click', closeWithClick);
  }

  function scrollOl() {
    if (!isOpen) return;
    if (selected === -1) return;
    const olHeight = ol.current!.getBoundingClientRect().height;
    const liHeight = ol.current!.scrollHeight / list.length;
    const centerTop = olHeight / 2 - liHeight / 2;
    const selectedTop = liHeight * selected;
    ol.current!.scroll({top: selectedTop - centerTop, left: 0, behavior: 'smooth'});
  }

  useEffect(focus, [inFocus]);
  useEffect(setupWindowClick, [isOpen]);
  useEffect(scrollOl, [selected]);
  useEffect(() => setSelected(index), [index])

  return (
    <Root>
      <label>
        {children}
        <div>
          <input type="text" value={value || list[selected] || ''} readOnly={readOnly} onChange={onChange} ref={input} onClick={e => e.stopPropagation()} onMouseDown={() => toggle()} onFocus={() => open()} onKeyDown={e => watchKeyboard(e)} placeholder={placeholder} />
          {readOnly && <button className="suffix" onClick={e => e.stopPropagation()} onMouseDown={() => toggle()} tabIndex={-1}><ChevronDownIcon /></button>}
        </div>
      </label>
      {isOpen && (
        <ol ref={ol}>
          {list.map((it, i) => (
            <li key={i} onClick={() => select(i)} className={i === selected ? 'selected' : ''}>{it}</li>
          ))}
        </ol>
      )}
    </Root>
  );

};

export { SelectInput };
